import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CircleHelp, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import Toggle from '@/components/ui/Toggle';
import Tooltip from '@/components/ui/Tooltip';
import {
  ChannelAutomation,
  ChannelAutomationPayload,
} from '@/services/channelAutomationService';
import {
  useChannelAutomations,
  useCreateChannelAutomation,
  useTestChannelAutomationRule,
  useUpdateChannelAutomation,
} from '@/hooks/useChannelAutomations';
import { useIptvProviders } from '@/hooks/useIptvProviders';
import {
  useFlussonicServers,
  useFlussonicServerStreams,
} from '@/hooks/useFlussonicServers';
import { useTVChannelsForDropdown } from '@/hooks/useTVChannel';
import { ChannelAutomationRuleTestResponse } from '@/services/channelAutomationService';

type ScheduleType = 'minutes' | 'hours' | 'days' | 'dailyAt';

interface RuleFormState {
  enabled: boolean;
  matchRegex: string;
  playlistGroup: string;
  tvChannelId: string;
  flussonicStreamName: string;
  channelTitleTemplate: string;
  appendMatchSuffix: boolean;
}

interface AutomationFormState {
  name: string;
  enabled: boolean;
  iptvProviderId: string;
  flussonicServerId: string;
  timezone: string;
  notes: string;
  scheduleType: ScheduleType;
  interval: number;
  dailyTime: string;
  rules: RuleFormState[];
}

const emptyRule: RuleFormState = {
  enabled: true,
  matchRegex: '',
  playlistGroup: '',
  tvChannelId: '',
  flussonicStreamName: '',
  channelTitleTemplate: '',
  appendMatchSuffix: true,
};

const emptyForm: AutomationFormState = {
  name: '',
  enabled: true,
  iptvProviderId: '',
  flussonicServerId: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  notes: '',
  scheduleType: 'dailyAt',
  interval: 1,
  dailyTime: '06:00',
  rules: [{ ...emptyRule }],
};

const buildCron = (form: AutomationFormState) => {
  const interval = Math.max(1, Number(form.interval) || 1);
  if (form.scheduleType === 'minutes') return `0 */${interval} * * * *`;
  if (form.scheduleType === 'hours') return `0 0 */${interval} * * *`;
  if (form.scheduleType === 'days') return `0 0 0 */${interval} * *`;
  const [hour = '0', minute = '0'] = form.dailyTime.split(':');
  return `0 ${Number(minute)} ${Number(hour)} * * *`;
};

const inferSchedule = (automation: ChannelAutomation): Pick<AutomationFormState, 'scheduleType' | 'interval' | 'dailyTime'> => {
  const parts = automation.cronExpression.split(' ');
  if (parts.length === 6 && parts[1]?.startsWith('*/')) {
    return { scheduleType: 'minutes', interval: Number(parts[1].slice(2)) || 1, dailyTime: '06:00' };
  }
  if (parts.length === 6 && parts[2]?.startsWith('*/')) {
    return { scheduleType: 'hours', interval: Number(parts[2].slice(2)) || 1, dailyTime: '06:00' };
  }
  if (parts.length === 6 && parts[3]?.startsWith('*/')) {
    return { scheduleType: 'days', interval: Number(parts[3].slice(2)) || 1, dailyTime: '06:00' };
  }
  return {
    scheduleType: 'dailyAt',
    interval: 1,
    dailyTime: parts.length === 6
      ? `${String(Number(parts[2] ?? 0)).padStart(2, '0')}:${String(Number(parts[1] ?? 0)).padStart(2, '0')}`
      : '06:00',
  };
};

const toPayload = (form: AutomationFormState): ChannelAutomationPayload => ({
  name: buildAutomationName(form),
  enabled: form.enabled,
  iptvProviderId: form.iptvProviderId,
  flussonicServerId: form.flussonicServerId,
  cronExpression: buildCron(form),
  timezone: form.timezone,
  notes: form.notes,
  rules: form.rules.map((rule, index) => ({
    displayOrder: index,
    enabled: true,
    matchRegex: rule.matchRegex,
    playlistGroup: rule.playlistGroup || null,
    tvChannelId: rule.tvChannelId,
    flussonicStreamName: rule.flussonicStreamName,
    channelTitleTemplate: rule.channelTitleTemplate || null,
    appendMatchSuffix: rule.appendMatchSuffix,
  })),
});

const buildAutomationName = (form: AutomationFormState) => {
  const firstRule = form.rules.find((rule) => rule.channelTitleTemplate.trim() || rule.flussonicStreamName.trim());
  return firstRule?.channelTitleTemplate.trim()
    || firstRule?.flussonicStreamName.trim()
    || `Automatización ${new Date().toISOString()}`;
};

const FieldLabel = ({ label, help }: { label: string; help: string }) => (
  <div className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700">
    <span>{label}</span>
    <Tooltip content={help}>
      <CircleHelp className="h-4 w-4 cursor-help text-gray-400" />
    </Tooltip>
  </div>
);

const TVChannelAutomationForm = () => {
  const navigate = useNavigate();
  const { automationId } = useParams();
  const isEditing = Boolean(automationId);
  const { data: automations = [], isLoading: automationsLoading } = useChannelAutomations();
  const { data: providers = [] } = useIptvProviders();
  const { data: servers = [] } = useFlussonicServers();
  const { data: tvChannels = [] } = useTVChannelsForDropdown();
  const createMutation = useCreateChannelAutomation();
  const updateMutation = useUpdateChannelAutomation();
  const testRuleMutation = useTestChannelAutomationRule();

  const [form, setForm] = useState<AutomationFormState>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ruleTestResults, setRuleTestResults] = useState<Record<number, ChannelAutomationRuleTestResponse>>({});
  const [ruleTestErrors, setRuleTestErrors] = useState<Record<number, string>>({});
  const [testingRuleIndex, setTestingRuleIndex] = useState<number | null>(null);
  const [newStreamRuleIndex, setNewStreamRuleIndex] = useState<number | null>(null);
  const [newStreamName, setNewStreamName] = useState('');

  const selectedAutomation = useMemo(
    () => automations.find((automation) => automation.id === automationId),
    [automations, automationId]
  );

  const { data: streams = [], isFetching: streamsFetching } = useFlussonicServerStreams(form.flussonicServerId);
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!selectedAutomation) return;
    const schedule = inferSchedule(selectedAutomation);
    setForm({
      name: selectedAutomation.name,
      enabled: selectedAutomation.enabled,
      iptvProviderId: selectedAutomation.iptvProviderId,
      flussonicServerId: selectedAutomation.flussonicServerId,
      timezone: emptyForm.timezone,
      notes: selectedAutomation.notes || '',
      scheduleType: schedule.scheduleType,
      interval: schedule.interval,
      dailyTime: schedule.dailyTime,
      rules: selectedAutomation.rules.length ? selectedAutomation.rules.slice(0, 1).map((rule) => ({
        enabled: true,
        matchRegex: rule.matchRegex,
        playlistGroup: rule.playlistGroup || '',
        tvChannelId: rule.tvChannelId,
        flussonicStreamName: rule.flussonicStreamName,
        channelTitleTemplate: rule.channelTitleTemplate || '',
        appendMatchSuffix: rule.appendMatchSuffix,
      })) : [{ ...emptyRule }],
    });
  }, [selectedAutomation]);

  const providerOptions = providers.map((provider) => ({ value: provider.id, label: provider.name }));
  const serverOptions = servers.map((server) => ({ value: server.id, label: server.name }));
  const channelOptions = tvChannels.map((channel) => ({
    value: channel.id,
    label: `${channel.channelNumber} - ${channel.title}`,
  }));
  const streamOptions = streams.map((stream) => ({
    value: stream.name,
    label: stream.title ? `${stream.name} - ${stream.title}` : stream.name,
  }));

  const updateRule = (index: number, patch: Partial<RuleFormState>) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, currentIndex) => currentIndex === index ? { ...rule, ...patch } : rule),
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const missingRule = form.rules.find((rule) => !rule.tvChannelId || !rule.flussonicStreamName);
    if (missingRule) {
      setSubmitError('Selecciona un canal existente y un stream Flussonic para cada regla.');
      return;
    }

    try {
      const payload = toPayload(form);
      if (isEditing && automationId) {
        await updateMutation.mutateAsync({ id: automationId, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate('/content/tv-channels/automations');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'No se pudo guardar la automatización');
    }
  };

  const openNewStreamModal = (ruleIndex: number) => {
    setNewStreamRuleIndex(ruleIndex);
    setNewStreamName('');
  };

  const closeNewStreamModal = () => {
    setNewStreamRuleIndex(null);
    setNewStreamName('');
  };

  const saveNewStream = () => {
    const streamName = newStreamName.trim();
    if (!streamName || newStreamRuleIndex === null) {
      return;
    }

    updateRule(newStreamRuleIndex, { flussonicStreamName: streamName });
    closeNewStreamModal();
  };

  const testRule = async (index: number) => {
    const rule = form.rules[index];
    if (!form.iptvProviderId || !rule.matchRegex.trim()) {
      setRuleTestErrors((prev) => ({
        ...prev,
        [index]: 'Selecciona una lista y escribe un regex para probar la regla.',
      }));
      return;
    }

    setTestingRuleIndex(index);
    setRuleTestErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setRuleTestResults((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    try {
      const result = await testRuleMutation.mutateAsync({
        iptvProviderId: form.iptvProviderId,
        matchRegex: rule.matchRegex,
        playlistGroup: rule.playlistGroup || null,
        channelTitleTemplate: rule.channelTitleTemplate || null,
        appendMatchSuffix: rule.appendMatchSuffix,
      });
      setRuleTestResults((prev) => ({ ...prev, [index]: result }));
    } catch (error) {
      setRuleTestErrors((prev) => ({
        ...prev,
        [index]: error instanceof Error ? error.message : 'No se pudo probar la regla',
      }));
    } finally {
      setTestingRuleIndex(null);
    }
  };

  const closeRuleTestResult = (index: number) => {
    setRuleTestResults((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setRuleTestErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  if (isEditing && automationsLoading) {
    return (
      <TVChannelsLayout>
        <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
          Cargando automatización...
        </div>
      </TVChannelsLayout>
    );
  }

  const ruleIndex = 0;
  const rule = form.rules[ruleIndex] ?? emptyRule;
  const ruleStreamOptions = rule.flussonicStreamName && !streamOptions.some((option) => option.value === rule.flussonicStreamName)
    ? [{ value: rule.flussonicStreamName, label: `${rule.flussonicStreamName} (nuevo)` }, ...streamOptions]
    : streamOptions;

  return (
    <TVChannelsLayout>
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Link to="/content/tv-channels/automations" className="mb-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Volver a automatizaciones
          </Link>

          <form onSubmit={handleSubmit} className="border border-gray-200 bg-white p-5 shadow">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h1 className="text-xl font-semibold text-gray-900">Nueva automatización</h1>
              <div>
                <FieldLabel label="Activa" help="Define si esta automatización puede ejecutarse manualmente y por agenda." />
                <Toggle
                  checked={form.enabled}
                  onChange={(checked) => setForm((prev) => ({ ...prev, enabled: checked }))}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel label="Título base (opcional)" help="Nombre base que recibirá el canal. Si se deja vacío, se conservará el título actual del canal existente. Si está activo anexar sufijo, se agregará el texto restante del canal encontrado en la lista." />
                  <Input value={form.rules[0]?.channelTitleTemplate ?? ''} onChange={(e) => updateRule(0, { channelTitleTemplate: e.target.value })} placeholder="MLB Game 1" disabled={isSubmitting} />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel label="Ejecución" help="Frecuencia con la que se ejecutará la automatización. El sistema la guarda como cron internamente." />
                    <Select
                      value={form.scheduleType}
                      onChange={(e) => setForm((prev) => ({ ...prev, scheduleType: e.target.value as ScheduleType }))}
                      options={[
                        { value: 'minutes', label: 'Cada X minutos' },
                        { value: 'hours', label: 'Cada X horas' },
                        { value: 'days', label: 'Cada X días' },
                        { value: 'dailyAt', label: 'Todos los días a una hora' },
                      ]}
                    />
                  </div>
                  {form.scheduleType === 'dailyAt' ? (
                    <div>
                      <FieldLabel label="Hora local" help="Hora del navegador en la que se ejecutará diariamente. El backend convierte la próxima ejecución a UTC." />
                      <Input type="time" value={form.dailyTime} onChange={(e) => setForm((prev) => ({ ...prev, dailyTime: e.target.value }))} required disabled={isSubmitting} />
                    </div>
                  ) : (
                    <div>
                      <FieldLabel label="Intervalo" help="Cantidad de minutos, horas o días entre ejecuciones, según el tipo de ejecución seleccionado." />
                      <Input type="number" min={1} value={form.interval} onChange={(e) => setForm((prev) => ({ ...prev, interval: Number(e.target.value) || 1 }))} required disabled={isSubmitting} />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 border-t border-gray-200 pt-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <FieldLabel label="Lista" help="Lista IPTV desde donde se leerán los canales y sus URLs fuente." />
                    <Select value={form.iptvProviderId} onChange={(e) => setForm((prev) => ({ ...prev, iptvProviderId: e.target.value }))} options={providerOptions} placeholder="Selecciona una lista" required disabled={isSubmitting} />
                  </div>
                  <div>
                    <FieldLabel label="Texto a buscar" help="Texto simple para encontrar el canal dentro de la lista. No se interpreta como regex. Ejemplo: MLB 1 |." />
                    <Input value={rule.matchRegex} onChange={(e) => updateRule(ruleIndex, { matchRegex: e.target.value })} placeholder="MLB ALT 01|MLB 18" required disabled={isSubmitting} />
                  </div>
                  <div>
                    <FieldLabel label="Grupo (opcional)" help="Si se especifica, la regla solo buscará coincidencias dentro de ese group-title de la lista M3U." />
                    <Input value={rule.playlistGroup} onChange={(e) => updateRule(ruleIndex, { playlistGroup: e.target.value })} disabled={isSubmitting} />
                  </div>
                  <div>
                    <FieldLabel label="Canal existente" help="Canal ya creado en la app que será actualizado por la automatización. La automatización no crea canales nuevos." />
                    <AutocompleteSelect
                      value={rule.tvChannelId}
                      onChange={(value) => updateRule(ruleIndex, { tvChannelId: value })}
                      options={channelOptions}
                      placeholder="Selecciona un canal"
                      disabled={isSubmitting}
                      emptyMessage="No hay canales disponibles"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => testRule(ruleIndex)}
                    loading={testingRuleIndex === ruleIndex}
                    disabled={isSubmitting || testingRuleIndex !== null || !form.iptvProviderId || !rule.matchRegex.trim()}
                  >
                    Probar regla
                  </Button>
                </div>

                {(ruleTestResults[ruleIndex] || ruleTestErrors[ruleIndex]) && (
                  <div className={`rounded-md p-3 text-sm ${ruleTestResults[ruleIndex]?.matched ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        {ruleTestErrors[ruleIndex] ? (
                          <p>{ruleTestErrors[ruleIndex]}</p>
                        ) : (
                          <>
                            <p className="font-medium">
                              {ruleTestResults[ruleIndex].matched ? 'Coincidencia encontrada' : 'Sin coincidencias'}
                            </p>
                            <p>{ruleTestResults[ruleIndex].message}</p>
                            <p>Elementos leídos: {ruleTestResults[ruleIndex].totalItems}</p>
                            {ruleTestResults[ruleIndex].matchedPlaylistTitle && (
                              <p>Título: {ruleTestResults[ruleIndex].matchedPlaylistTitle}</p>
                            )}
                            {ruleTestResults[ruleIndex].matchedPlaylistGroup && (
                              <p>Grupo: {ruleTestResults[ruleIndex].matchedPlaylistGroup}</p>
                            )}
                            {ruleTestResults[ruleIndex].matchedPrefix && (
                              <p>Match: {ruleTestResults[ruleIndex].matchedPrefix}</p>
                            )}
                            {ruleTestResults[ruleIndex].suffix && (
                              <p>Sufijo: {ruleTestResults[ruleIndex].suffix}</p>
                            )}
                            {ruleTestResults[ruleIndex].channelTitle && (
                              <p>Título resultante: {ruleTestResults[ruleIndex].channelTitle}</p>
                            )}
                            {ruleTestResults[ruleIndex].matchedPlaylistUrl && (
                              <p className="break-all">Fuente: {ruleTestResults[ruleIndex].matchedPlaylistUrl}</p>
                            )}
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => closeRuleTestResult(ruleIndex)}
                        className="rounded p-1 text-current opacity-70 hover:bg-black/5 hover:opacity-100"
                        aria-label="Cerrar resultado de prueba"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
                  <div>
                    <FieldLabel label="Servidor Flussonic" help="Instancia de Flussonic donde se creará o actualizará el stream usado por este canal." />
                    <Select
                      value={form.flussonicServerId}
                      onChange={(e) => setForm((prev) => ({ ...prev, flussonicServerId: e.target.value, rules: prev.rules.map((currentRule) => ({ ...currentRule, flussonicStreamName: '' })) }))}
                      options={serverOptions}
                      placeholder="Selecciona un servidor"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <FieldLabel label={streamsFetching ? 'Stream Flussonic (cargando...)' : 'Stream Flussonic'} help="Stream de Flussonic que recibirá la URL fuente encontrada en la lista. Puedes seleccionar uno existente o crear un nombre nuevo." />
                    <AutocompleteSelect
                      value={rule.flussonicStreamName}
                      onChange={(value) => updateRule(ruleIndex, { flussonicStreamName: value })}
                      options={ruleStreamOptions}
                      placeholder={form.flussonicServerId ? 'Selecciona un stream' : 'Selecciona un servidor primero'}
                      disabled={isSubmitting || !form.flussonicServerId || streamsFetching}
                      loading={streamsFetching}
                      emptyMessage="No hay streams disponibles"
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={() => openNewStreamModal(ruleIndex)} disabled={isSubmitting || !form.flussonicServerId} className="h-10 whitespace-nowrap">
                    Nuevo Stream
                  </Button>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div>
                    <FieldLabel label="Anexar sufijo después del match" help="Cuando está activo, el sufijo encontrado en la lista se agrega al título base del canal." />
                    <Toggle
                      checked={rule.appendMatchSuffix}
                      onChange={(checked) => updateRule(ruleIndex, { appendMatchSuffix: checked })}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </div>

            {submitError && <div className="mt-5 rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>}

            <div className="mt-5 flex justify-end gap-2 border-t border-gray-200 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/content/tv-channels/automations')} disabled={isSubmitting}>Cancelar</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>

          <Modal
            isOpen={newStreamRuleIndex !== null}
            onClose={closeNewStreamModal}
            title="Nuevo Stream"
            size="md"
            closeOnBackdrop
            closeOnEsc
          >
            <div className="space-y-4 px-2 py-2">
              <Input
                label="Nombre del stream"
                value={newStreamName}
                onChange={(e) => setNewStreamName(e.target.value)}
                placeholder="mlb-game-1"
                autoFocus
              />
              <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                <Button type="button" variant="outline" onClick={closeNewStreamModal}>
                  Cancelar
                </Button>
                <Button type="button" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={saveNewStream} disabled={!newStreamName.trim()}>
                  Guardar
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelAutomationForm;
